"use client"

import { APP_ENV } from "@src/utils/environment"
import TonConnect from "@tonconnect/sdk"
import type { RequiredFeatures } from "@tonconnect/sdk"
import dynamic from "next/dynamic"
import { type ReactNode, useMemo } from "react"

export const TON_WALLETS_LIST_SOURCE =
  "https://raw.githubusercontent.com/ton-connect/wallets-list/main/wallets-v2.json"

const walletsRequiredFeatures: RequiredFeatures = {
  signData: { types: ["text"] },
}

export function getTonManifestUrl() {
  return APP_ENV === "development"
    ? // TON Keeper extension does not load manifest from http://localhost, so we fallback to the demo app
      "https://ton-connect.github.io/demo-dapp-with-wallet/tonconnect-manifest.json"
    : "https://near.com/tonconnect-manifest.json"
}

export function createTonConnector() {
  return new TonConnect({
    manifestUrl: getTonManifestUrl(),
    walletsListSource: TON_WALLETS_LIST_SOURCE,
    walletsRequiredFeatures,
    analytics: { mode: "off" },
  })
}

// Disable SSR for TonConnectUIProvider to prevent hydration mismatch
// The TON Connect library adds ontouchstart attribute to body during initialization,
// which causes React hydration errors when server and client DOM don't match
const TonConnectUIProvider = dynamic(
  () =>
    import("@tonconnect/ui-react").then((mod) => {
      function TonConnectUIProviderClient({
        children,
      }: {
        children: ReactNode
      }) {
        const connector = useMemo(() => createTonConnector(), [])

        return (
          <mod.TonConnectUIProvider
            connector={connector}
            walletsRequiredFeatures={walletsRequiredFeatures}
            analytics={{ mode: "off" }}
          >
            {children}
          </mod.TonConnectUIProvider>
        )
      }

      return TonConnectUIProviderClient
    }),
  { ssr: false }
)

function TonConnectUIProviderWrapper({ children }: { children: ReactNode }) {
  return <TonConnectUIProvider>{children}</TonConnectUIProvider>
}

export { TonConnectUIProviderWrapper as TonConnectUIProvider }
